/**
 * NavMe shared types.
 * Coordinate convention: all POI / graph coordinates are in MultiSet MAP SPACE
 * (metres, right-handed, as returned by VPS localization). They are converted
 * to Three.js world space at render time via the worldFromMap matrix.
 */

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

// ---------- POIs ----------

export interface POI {
  id: string;
  name: string;
  category: string;
  floor: number;
  /** Position in map space (metres). */
  position: Vec3;
  /** Nearest navigation graph node (entry point for routing). */
  nodeId: string;
  description?: string;
  /** Emoji or icon URL shown in the picker. */
  icon?: string;
  /** Optional MultiSet anchor reference for future anchor-based placement. */
  anchorRef?: string;
  /** Optional media attachment for this POI. 'link' renders an AR button that opens the URL; 'audio' plays narration on approach. */
  mediaType?: 'image' | 'video' | 'model' | 'link' | 'audio';
  /** Attachment URL: uploaded file, direct media URL (.mp4/.jpg/.glb), or webpage. */
  mediaUrl?: string;
  /** Campaign window (ISO timestamps): media shows only within [start, end]. */
  mediaStart?: string;
  mediaEnd?: string;
  /**
   * Per-POI visitor capture: when true, arriving at this POI auto-captures a
   * photo + video (venue_settings.capture_secs) for the Dashboard Captures
   * card. The venue-level 'capture' feature flag must ALSO be on — both gates
   * are checked at arrival in main.ts. Default off (opt-in per POI).
   */
  capture?: boolean;
  /** 🛍 Retail pack: promo text shown on the picker's offers rail (feature 'offers'). */
  offer?: string;
  /** Offer expiry (ISO timestamp) — the offer hides itself after this. Empty = evergreen. */
  offerUntil?: string;
  /** 🏥 Healthcare pack: who this destination is for (feature 'audienceModes').
   *  Undefined/'all' shows for everyone; otherwise filtered by the ?aud= embed param. */
  audience?: 'all' | 'patient' | 'visitor' | 'staff';
}

// ---------- Navigation graph ----------

export interface NavNode {
  id: string;
  floor: number;
  position: Vec3;
  /** 'walk' | 'elevator' | 'stairs' | 'escalator' — extension point for multi-floor. */
  kind?: string;
}

export interface NavEdge {
  from: string;
  to: string;
  /** Optional explicit weight; defaults to euclidean distance. */
  weight?: number;
  /** Whether wheelchair-accessible. Used when accessibleOnly routing is on. */
  accessible?: boolean;
  /** Extension point: 'walk' | 'elevator' | 'stairs' | 'escalator' | 'restricted' */
  kind?: string;
  bidirectional?: boolean;
}

export interface NavGraph {
  nodes: NavNode[];
  edges: NavEdge[];
}

export interface Route {
  /** Ordered node list from start to destination. */
  nodes: NavNode[];
  /** Total length in metres. */
  distance: number;
  destination: POI;
}

// ---------- Localization ----------

export enum LocalizationState {
  NotStarted = 'Not started',
  PermissionNeeded = 'Camera permission needed',
  Localizing = 'Localizing…',
  Localized = 'Localized',
  TrackingWeak = 'Tracking weak',
  LostTracking = 'Lost tracking',
  Relocalizing = 'Relocalizing…',
  Error = 'Error',
}

// ---------- App config / credentials ----------

export type MultisetMapType = 'map' | 'map-set';

export interface NavMeConfig {
  clientId: string;
  clientSecret: string;
  mapType: MultisetMapType;
  code: string;
  /**
   * True when MultiSet auth goes through the multiset-token Edge Function
   * broker (clientId carries the map code; real credentials stay server-side
   * — customer BYO or platform account, resolved by the broker).
   */
  brokered?: boolean;
  /**
   * Compass-mode ONLY (cameraArSession.ts / CompassOverlay.ts) — the true
   * compass bearing (0-360, clockwise from North) that corresponds to the
   * map's own "-Z / forward" direction. The WebXR/native paths never need
   * this because continuous inside-out tracking already ties the device's
   * orientation to the map frame; the compass fallback has no such
   * continuous tracking, only periodic absolute-position fixes, so we need
   * this offset to translate a map-space bearing into a real-world compass
   * bearing the device's magnetometer can be compared against.
   * TODO(production): don't hardcode this — calibrate per venue. Simplest
   * approach: stand at a known map point facing a known compass direction,
   * read the device heading, and set mapNorthOffsetDeg = heading -
   * bearingDeg(from map origin toward where you're facing). Defaults to 0,
   * which is almost certainly wrong for a real venue and will make the
   * compass arrow point in a consistent but incorrect direction until
   * calibrated.
   */
  mapNorthOffsetDeg?: number;
  /**
   * WebXR mode ONLY (multisetClient.ts). Passed straight through to the
   * MultiSet SDK's `ThreeAdapter({ showMesh })` option — a real, documented
   * constructor flag (default false) that renders the localized MultiSet
   * map mesh in the scene. Off by default to keep the mobile render loop
   * light; it's a constructor-time option (no runtime toggle exists in the
   * SDK), so this is read once when the WebXR session is prepared. Has no
   * effect in camera mode (8th Wall doesn't have access to MultiSet's mesh
   * data via the REST API this app uses for that path).
   */
  showLocalizationMesh?: boolean;
}

// ---------- App state machine ----------

export enum AppScreen {
  Landing = 'landing',
  Setup = 'setup',
  AR = 'ar',
}

export interface PoseSnapshot {
  /** Camera position in map space (metres). */
  mapPosition: Vec3;
  /** Camera position in world space. */
  worldPosition: Vec3;
  /** Heading (radians, around +Y) in map space. */
  headingRad: number;
}
